import { ChangeDetectorRef, OnDestroy, Pipe, PipeTransform } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, Subscription } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { LocalizedValue } from '@celum/core/model';
import { isEqualSimpleObject, isObject } from '@celum/core/util';

import { instantTranslate } from './translate-instant';
import { TranslationHelper } from './translation-helper';
import { currentLocaleStream } from './translation-util';

interface TranslationResult {
  result: string;
  input: string | LocalizedValue;
  paramLanguage: string;
  isGuessedTranslation: boolean;
  interpolationParams?: object;
}

interface LanguagePipeOptions {
  /** Specific language which should be used. If not set, the currently active language will be used. **/
  languageKey?: string;
  /** Flag whether the {@see fallbackLocale} and default locale (as set in the translate service) should be used if the specific/current languages are not set **/
  useFallback?: boolean;
  /** Locale to use if the {@see languageKey} or current locale are not set **/
  fallbackLocale?: string;
  /** Flag whether after checking all previous available languages (current, default, fallback), if just any first available locale should be used. Default: false **/
  useAnyFallback?: boolean;
  interpolationParams?: object;
}

/**
 * Picks right language from language object or translates string
 *  - Called with plain string: Route through translate service and serve translated value which updates automatically on language changes
 *  - Called with language object ({ de: 'Hallo', en: 'Hello' }) - picks right language according to user/default - language. Does not translate result!
 *
 *  'NOVA.CUSTOM.KEY' => 'Resolved key value'
 *  { de: 'Hallo', en: 'Hello' } => 'Hello' (if language is english)
 *  { de: 'NOVA.KEY.GERMAN', en: 'NOVA.KEY.ENGLISH' } => 'NOVA.KEY.ENGLISH' => this will not be translated - chain translate pipe if it has to be.
 */
@Pipe({
  name: 'language',
  pure: false // has to be false as it can be called with same params but current language changed
})
export class LanguagePipe implements OnDestroy, PipeTransform {
  private currentResult: TranslationResult;
  private changeSubscription: Subscription;

  constructor(
    private changeDetector: ChangeDetectorRef,
    private translateService: TranslateService
  ) {}

  /**
   * Transforms the given input based on the provided options, applying localization or other transformations.
   *
   * @param input - The input value to be transformed. It can be a plain string or a localized value.
   * @param options - Optional parameter for specifying transformation options such as target language or formatting details.
   * @return a 'best-guess' string + change detection will be called again if value changes or best guess was not correct
   */
  public transform(input: string | LocalizedValue, options?: LanguagePipeOptions): string;
  /**
   * Transforms the input into a localized string based on the provided parameters.
   * @param input string or localized value
   * @param languageKey - only for LocalizedValue!! - language which should be used for translating (if not set, ui-language is used)
   * @param useFallback Specify if there should be fallbacks if no value for given language is found
   * @param interpolationParams which will be used if string is passed as string
   * @param fallbackLocale - The locale to use as a fallback when the specified language key does not have a value.
   * @return a 'best-guess' string + change detection will be called again if value changes or best guess was not correct
   */
  public transform(input: string | LocalizedValue, languageKey?: string, useFallback?: boolean, interpolationParams?: object, fallbackLocale?: string): string;
  public transform(
    input: string | LocalizedValue,
    languageKeyOrOptions?: string | LanguagePipeOptions,
    useFallback = true,
    interpolationParams?: object,
    fallbackLocale?: string
  ): string {
    if (!input) {
      return '';
    }

    // Check if the second parameter is an options object
    let options: LanguagePipeOptions;
    if (this.isLanguagePipeOptions(languageKeyOrOptions)) {
      const defaults = { useFallback: true, useAnyFallback: false };
      options = { ...defaults, ...languageKeyOrOptions };
    } else {
      options = { languageKey: languageKeyOrOptions, useFallback, interpolationParams, fallbackLocale };
    }

    const alreadyEvaluated = LanguagePipe.isAlreadyEvaluated(this.currentResult, input, options.languageKey, options.interpolationParams);
    if (alreadyEvaluated && !this.currentResult.isGuessedTranslation) {
      return this.currentResult.result || '';
    }

    // The instant translation is not always 100% correct as it's using the translate service's instant translation for strings or assumes the language
    // variants for objects. But this should be fine for 90% of the cases, so we set the value, start the streams and only run change detection again if
    // the 'real' value is different from the guessed one. (see updateValue - method)
    const instantTranslation = this.getBestGuessInstantTranslation(input, options);
    this.updateValue(instantTranslation, input, options.languageKey, true, options.interpolationParams);

    this.changeSubscription && this.changeSubscription.unsubscribe();
    this.changeSubscription = this.asyncResult(input, options).subscribe(translated =>
      this.updateValue(translated, input, options.languageKey, false, options.interpolationParams)
    );

    return this.currentResult.result || '';
  }

  public ngOnDestroy(): void {
    this.changeSubscription && this.changeSubscription.unsubscribe();
  }

  /**
   * Returns the stream of results which is updating if display language or default language changes.
   * @param value Input value (string or localized value)
   * @param options Options to configure how resolving and fallbacks should be managed
   */
  private asyncResult(value: string | LocalizedValue, options?: LanguagePipeOptions): Observable<string> {
    if (!value) {
      return of('');
    }

    let translated$: Observable<string>;
    if (typeof value === 'string') {
      translated$ = this.translateService.stream(value, options?.interpolationParams);
    } else {
      translated$ = currentLocaleStream(this.translateService).pipe(
        map(displayLang =>
          TranslationHelper.evaluateLocalizedValue(
            value,
            options?.languageKey,
            displayLang,
            this.translateService.getFallbackLang(),
            options?.useFallback,
            options?.fallbackLocale,
            options?.useAnyFallback
          )
        )
      );
    }

    return translated$.pipe(distinctUntilChanged());
  }

  private getBestGuessInstantTranslation(value: string | LocalizedValue, options: LanguagePipeOptions): string {
    return instantTranslate(
      this.translateService,
      value,
      this.translateService.getCurrentLang(),
      this.translateService.getFallbackLang(),
      options?.languageKey,
      options?.useFallback,
      options?.interpolationParams,
      options?.fallbackLocale,
      options?.useAnyFallback
    );
  }

  private updateValue(result: string, input: string | LocalizedValue, languageKey: string, isGuessedTranslation: boolean, interpolationParams?: object): void {
    const valueChanged = !this.currentResult || this.currentResult.result !== result;

    this.currentResult = {
      input,
      result,
      paramLanguage: languageKey,
      isGuessedTranslation,
      interpolationParams
    };

    // Only run change detection if the value changed and the result is not guessed (guessed result is anyway returned immediately)
    if (valueChanged && !isGuessedTranslation) {
      this.changeDetector && this.changeDetector.markForCheck();
    }
  }

  private isLanguagePipeOptions(value: any): value is LanguagePipeOptions {
    return isObject(value);
  }

  private static isAlreadyEvaluated(
    currentResult: TranslationResult,
    input: string | LocalizedValue,
    paramLanguage: string,
    interpolationParams?: object
  ): boolean {
    if (!currentResult) {
      return false;
    }

    const isInterpolationParamsEqual = !interpolationParams || isEqualSimpleObject(interpolationParams, currentResult.interpolationParams);
    if (isInterpolationParamsEqual && currentResult.paramLanguage === paramLanguage) {
      return typeof input === 'string' ? input === currentResult.input : isEqualSimpleObject(input, currentResult.input);
    }

    return false;
  }
}
